export const getModuleWorkstreams = (config) => config?.workstreams || [];

export const getPrimaryWorkstreamKey = (config) => getModuleWorkstreams(config)[0]?.key || 'general';

export const buildWorkstreamState = (config, defaultValue = 'all', predicate = () => true) => (
    Object.fromEntries(
        getModuleWorkstreams(config)
            .filter(predicate)
            .map(workstream => [workstream.key, defaultValue])
    )
);

export const getMonthOptions = (count = 24) => {
    const months = [];
    const now = new Date();

    for (let index = 0; index < count; index++) {
        const date = new Date(now.getFullYear(), now.getMonth() + index, 1);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        months.push(`${year}-${month}`);
    }

    return months;
};

export const getModuleEntity = (config) => ({
    singular: config?.entity?.singular || 'Client',
    plural: config?.entity?.plural || config?.label || 'Clients',
    totalLabel: config?.entity?.totalLabel || `Total ${config?.label || 'Clients'}`,
    addLabel: config?.entity?.addLabel || 'Add Client',
    numberLabel: config?.entity?.numberLabel || 'Number',
    syncLabel: config?.entity?.syncLabel || 'Sync Data'
});

export const getWorkstreamByKey = (config, key) => (
    getModuleWorkstreams(config).find(workstream => workstream.key === key)
);

export const getWorkstreamColorClasses = (workstream = {}) => ({
    text: workstream.textColorClass || 'text-slate-700',
    badge: workstream.badgeColorClass || 'bg-slate-100 text-slate-700',
    filter: workstream.filterColorClass || 'bg-slate-50 border-slate-200 text-slate-700',
    dot: workstream.dotColorClass || 'bg-slate-400'
});

export const getExternalLinkSections = (config) => {
    if (Array.isArray(config?.externalLinkSections)) return config.externalLinkSections;
    if (config?.externalLinks) return [config.externalLinks];
    return [];
};

export const SERVICE_EXTERNAL_SECTION_ORDER = {
    'Client Folders': 0,
    'Charity Commission Links': 2,
    'Companies House Links': 2,
    'Inform Direct Links': 3,
    'Bright Manager Links': 4,
};

export const HIDDEN_SERVICE_SECTION_LABELS = new Set(['Companies House Links', 'Inform Direct Links']);

const SERVICE_EXTERNAL_RULES = {
    vat: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: false,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_hmrc', 'sp_vat', 'sp_vat_returns', 'sp_vat_returns_vat100']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_hmrc_main', 'documents_hmrc_vat', 'documents_hmrc_vat_correspondence', 'documents_hmrc_vat_returns']),
        },
    },
    payroll: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_payroll', 'sp_payroll_reports', 'sp_pension_regulator', 'sp_pension_regulator_returns']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_payroll_main', 'documents_payroll_correspondence', 'documents_payroll_reports']),
        },
    },
    pension_ae: {
        openSections: new Set(['Client Folders']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_pension_regulator', 'sp_pension_regulator_returns']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_payroll_main']),
        },
    },
    cis: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_hmrc']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_hmrc_main']),
        },
    },
    bookkeeping: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_management', 'sp_general', 'sp_accounts', 'sp_accounts_reports', 'sp_accounts_reports_annual', 'sp_accounts_bank']),
            'Bright Manager Links': new Set(['dashboard', 'documents']),
        },
    },
    sa_returns: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_personal_tax', 'sp_personal_tax_sa100']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_hmrc_main', 'documents_hmrc_pers_tax', 'documents_hmrc_pers_tax_correspondence', 'documents_hmrc_sa100']),
        },
    },
    ch_accounts: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_management', 'sp_general', 'sp_accounts', 'sp_accounts_reports', 'sp_accounts_reports_annual', 'sp_accounts_bank']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_accounts']),
        },
    },
    cotax: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_hmrc', 'sp_cotax', 'sp_cotax_ct600']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_hmrc_main', 'documents_hmrc_cotax', 'documents_hmrc_cotax_correspondence', 'documents_hmrc_ct600']),
        },
    },
    cfo: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_management', 'sp_general']),
            'Bright Manager Links': new Set(['dashboard', 'documents']),
        },
    },
    mgmt_accounts: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_management', 'sp_general', 'sp_accounts', 'sp_accounts_reports']),
            'Bright Manager Links': new Set(['dashboard', 'documents', 'documents_accounts']),
        },
    },
    gift_aid: {
        openSections: new Set(['Client Folders', 'Charity Commission Links']),
        openEngagedServices: false,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_charity_commission', 'sp_charity_commission_returns']),
            'Charity Commission Links': new Set(['overview', 'accounts_returns']),
        },
    },
    independent_exam: {
        openSections: new Set(['Client Folders', 'Charity Commission Links']),
        openEngagedServices: false,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_general', 'sp_charity_commission', 'sp_charity_commission_returns', 'sp_accounts', 'sp_accounts_reports']),
            'Charity Commission Links': new Set(['overview', 'financial_history', 'accounts_returns']),
        },
    },
    reg_address: {
        openSections: new Set(['Client Folders']),
        openEngagedServices: false,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_management', 'sp_cosec', 'sp_cosec_register']),
        },
    },
    consultation: {
        openSections: new Set(['Client Folders', 'Bright Manager Links']),
        openEngagedServices: true,
        linkAllowlist: {
            'Client Folders': new Set(['sp_main', 'sp_notes', 'sp_management', 'sp_general']),
            'Bright Manager Links': new Set(['dashboard', 'documents']),
        },
    },
};

export const getServiceExternalSectionBehavior = (serviceId) => SERVICE_EXTERNAL_RULES[serviceId] || null;

export const isServiceExternalSectionHidden = (sectionLabel = '') => HIDDEN_SERVICE_SECTION_LABELS.has(String(sectionLabel || ''));

export const isServiceSectionDefaultOpen = (serviceId, sectionLabel = '') => {
    const behavior = getServiceExternalSectionBehavior(serviceId);
    if (!behavior) return sectionLabel === 'Client Folders';
    return behavior.openSections.has(String(sectionLabel || ''));
};

export const getServiceScopedLinks = (serviceId, sectionLabel = '', links = []) => {
    const behavior = getServiceExternalSectionBehavior(serviceId);
    if (!behavior) return links;

    const allowedKeys = behavior.linkAllowlist?.[String(sectionLabel || '')];
    if (!allowedKeys) return [];

    return links.filter((link) => allowedKeys.has(String(link?.key || '')));
};

export const tryLaunchLocalFolderPath = async (path) => {
    if (!path) return false;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 900);

    try {
        const response = await fetch(
            `http://127.0.0.1:48124/open-folder?path=${encodeURIComponent(path)}`,
            {
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
                cache: 'no-store',
                signal: controller.signal,
            }
        );

        return response.ok;
    } catch {
        return false;
    } finally {
        window.clearTimeout(timeoutId);
    }
};

export const checkLocalFolderLauncher = async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 2000);

    try {
        const response = await fetch('http://127.0.0.1:48124/health', {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            cache: 'no-store',
            signal: controller.signal,
        });

        return response.ok;
    } catch {
        return false;
    } finally {
        window.clearTimeout(timeoutId);
    }
};

export const pollForLauncher = async (maxWaitMs = 14000, intervalMs = 1500) => {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
        // eslint-disable-next-line no-await-in-loop
        const ok = await checkLocalFolderLauncher();
        if (ok) return true;
    }
    return false;
};

export const restartLocalFolderLauncher = async () => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 1500);

    try {
        const response = await fetch('http://127.0.0.1:48124/restart-launcher', {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            cache: 'no-store',
            signal: controller.signal,
        });

        return response.ok;
    } catch {
        return false;
    } finally {
        window.clearTimeout(timeoutId);
    }
};

export const getAvailableExternalLinkSections = (config, record = {}) => (
    getExternalLinkSections(config)
        .map((section) => {
            const mainField = config?.fieldMappings?.mainField;
            const mainValue = String(record?.[mainField] || '').trim();
            const referenceField = section?.referenceField;
            const referenceValue = referenceField ? String(record?.[referenceField] || '').trim() : '';

            const sanitizeClientFolderName = (value) => (
                String(value || '')
                    .replace(/[\\/:*?"<>|]/g, '-')
                    .replace(/\s+/g, ' ')
                    .trim()
            );

            const parseFolderPair = (value) => {
                const raw = String(value || '').trim();
                if (!raw) return { letter: '', name: '' };

                const normalized = raw
                    .replace(/\\+/g, '/')
                    .replace(/^\/+|\/+$/g, '');

                const parts = normalized.split('/').filter(Boolean);
                if (parts.length === 0) return { letter: '', name: '' };

                if (parts.length === 1) {
                    const singleName = parts[0].trim();
                    return {
                        letter: (singleName.charAt(0) || '_').toUpperCase(),
                        name: singleName,
                    };
                }

                const letter = parts[0].trim();
                const name = parts.slice(1).join('/').trim();

                return {
                    letter: (letter.charAt(0) || name.charAt(0) || '_').toUpperCase(),
                    name,
                };
            };

            let clientFolderName = sanitizeClientFolderName(mainValue);
            let clientLetter = (clientFolderName.charAt(0).toUpperCase() || '_');

            if (section?.referenceFormat === 'client-folder-pair') {
                const parsedFolder = parseFolderPair(referenceValue);
                clientFolderName = sanitizeClientFolderName(parsedFolder.name);
                clientLetter = (sanitizeClientFolderName(parsedFolder.letter).charAt(0).toUpperCase() || '_');
            }

            const hasTemplateLinks = (section.links || []).some((link) => Boolean(link?.urlTemplate));
            const hasStandardLinks = (section.links || []).some((link) => !link?.urlTemplate);

            if (hasStandardLinks && (!section?.baseUrl || !referenceValue)) return null;
            if (hasTemplateLinks && (!clientFolderName || !clientLetter)) return null;

            const isTruthyFlag = (value) => (
                value === true
                || value === 1
                || value === '1'
                || value === 'true'
                || value === 'TRUE'
                || value === 'yes'
                || value === 'YES'
            );

            const isLinkVisible = (link) => {
                const anyFields = Array.isArray(link?.requiredAnyServiceFields) ? link.requiredAnyServiceFields : [];
                const allFields = Array.isArray(link?.requiredAllServiceFields) ? link.requiredAllServiceFields : [];
                const requiredModuleIds = Array.isArray(link?.requiredModuleIds) ? link.requiredModuleIds : [];
                const excludedModuleIds = Array.isArray(link?.excludedModuleIds) ? link.excludedModuleIds : [];

                if (requiredModuleIds.length > 0 && !requiredModuleIds.includes(config?.id)) {
                    return false;
                }

                if (excludedModuleIds.includes(config?.id)) {
                    return false;
                }

                if (anyFields.length > 0 && !anyFields.some((field) => isTruthyFlag(record?.[field]))) {
                    return false;
                }

                if (allFields.length > 0 && !allFields.every((field) => isTruthyFlag(record?.[field]))) {
                    return false;
                }

                return true;
            };

            const resolveTemplate = (template, link = {}) => {
                if (!template) return '';
                const shouldEncodeTemplateTokens = link?.encodeTemplateTokens !== false;
                const letterValue = shouldEncodeTemplateTokens ? encodeURIComponent(clientLetter) : clientLetter;
                const nameValue = shouldEncodeTemplateTokens ? encodeURIComponent(clientFolderName) : clientFolderName;

                return String(template)
                    .replaceAll('{ClientLetter}', letterValue)
                    .replaceAll('{ClientName}', nameValue);
            };

            const links = (section.links || [])
                .filter((link) => isLinkVisible(link))
                .map((link) => {
                    if (link?.urlTemplate) {
                        const resolvedUrl = resolveTemplate(link.urlTemplate, link);
                        const localPath = /^ms-explorer:/i.test(resolvedUrl)
                            ? resolvedUrl.replace(/^ms-explorer:/i, '')
                            : (/^file:\/\//i.test(resolvedUrl)
                                ? decodeURIComponent(resolvedUrl.replace(/^file:\/\//i, '').replace(/^\/+/, ''))
                                : '');

                        return {
                            ...link,
                            url: resolvedUrl,
                            localPath,
                            referenceValue,
                        };
                    }

                    return {
                        ...link,
                        url: `${(link.baseUrl || section.baseUrl)}${encodeURIComponent(referenceValue)}${link.suffix || ''}`,
                        referenceValue,
                    };
                });

            if (links.length === 0) return null;

            return {
                ...section,
                referenceValue,
                links,
            };
        })
        .filter(Boolean)
);
